import { useEffect, useMemo } from "react";
import type { ApiResponse, BooleanOperationResult } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { ReportInfo, UpdateReportsAuthorizationRequest } from "../../../../../../api/types/svTypes.ts";
import useUserContext from "../../../../../../context/useUserContext";
import type { ReportCompany } from "../../../sv/components/viewer/common/sharing/ShareReport.types.ts";
import ShareReportsBulk from "../../../sv/components/viewer/common/sharing/bulk/ShareReportsBulk.tsx";

interface Props {
  reports: ReportInfo[];
  doUpdateSharing: (request: UpdateReportsAuthorizationRequest) => Promise<ApiResponse<BooleanOperationResult>>;
  onShared: () => void;
  onClose: () => void;
}

export default function ShareReportsBulkWrapper({ reports, doUpdateSharing, onShared, onClose }: Props) {
  const { id, name, email, clients } = useUserContext();
  const { sendNotification } = useNotificationContext();

  const reportCompany = useMemo((): ReportCompany | undefined => {
    const client = clients.find((c) => c.clientCode === reports[0]?.clientCode);
    return client
      ? { clientCode: client.clientCode, name: client.title, logoMarkUrl: client.branding.logoMarkUrl }
      : undefined;
  }, [clients, reports]);

  useEffect(() => {
    if (!reportCompany) {
      sendNotification("Failed to load report company. Refresh page and try again.");
      onClose();
    }
  }, [reportCompany, sendNotification, onClose]);

  if (!reportCompany) {
    return null;
  }

  return (
    <ShareReportsBulk
      reports={reports}
      currentUser={{ id, name, email }}
      reportCompany={reportCompany}
      doUpdateSharing={doUpdateSharing}
      onShared={onShared}
      onClose={onClose}
    />
  );
}
